import { useGetUserGroupsQuery } from 'store/services/flow/userGroups';
import { useState, useMemo, useCallback, memo } from 'react';

import type { ModalsType } from 'types';
import { Tabs } from 'components/mui/common/Tabs';

import { ContactsTable } from 'components/user/Contacts/ContactsTable';
import { useTranslation } from 'react-i18next';
import { ActionModal } from 'components/mui/common/ActionModal';

export interface Props extends ModalsType {
  title: string;
  fetchUsersMethod?: 'all' | 'simple';
  formItem: Record<string, unknown>;
  onChange?: (selectedUsers: string[]) => void;
  onSubmit: (selectedUsers: string[]) => void;
  isMultiSelect?: boolean;
  roleIds?: string[];
  selectedOptions?: string[];
}

export const UserGroupsModal = memo(
  ({ open, title, fetchUsersMethod, onClose, onSubmit, onChange, isMultiSelect, roleIds, selectedOptions = [] }: Props) => {
    const { t } = useTranslation();
    const [selectedUsers, setSelectedUsers] = useState<string[]>(selectedOptions);
    const [selectedTab, setSelectedTab] = useState<string>(t('user.contacts.groups.all'));
    const { data: { userGroups = [] } = {} } = useGetUserGroupsQuery();

    const groups = useMemo(() => [{ id: '', name: t('user.contacts.groups.all'), isAuthor: false }, ...userGroups], [t, userGroups]);

    const handleSelect = useCallback(
      (users: string[]) => {
        const selectedIds = isMultiSelect ? users : users.filter((user) => !selectedOptions.includes(user));
        onChange?.(selectedIds);
        setSelectedUsers(selectedIds);
      },
      [selectedOptions, isMultiSelect, onChange]
    );

    const tabs = useMemo(() => {
      return groups.map(({ name, id }) => ({
        id: name,
        label: name,
        path: '',
        content: (
          <ContactsTable
            onSelectUser={handleSelect}
            hideCheckbox={false}
            defaultSelectedRows={selectedOptions}
            isMultiSelect={isMultiSelect}
            groupId={id}
            roleIds={roleIds}
            fetchUsersMethod={fetchUsersMethod || 'simple'}
          />
        ),
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, handleSelect, selectedOptions, isMultiSelect, roleIds]);

    return (
      <ActionModal
        open={open}
        title={title}
        onClose={onClose}
        onCancel={onClose}
        onSubmit={() => onSubmit(selectedUsers)}
        maxWidth="lg"
        submitText={t('user.contacts.update')}>
        <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedTab(tab as string)} />
      </ActionModal>
    );
  }
);
